import React, { useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';

import { useGetUploadedUpliftFiles } from '../../hooks/useGetUploadedUpliftFiles';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import useColumns from './columns';
import Pagination from '@/components/common/Pagination/Pagination';
import { UPLIFT_UPLOADED_FILES_PER_PAGE } from '@/config/common.config';
import { UpliftPaginatedList, UpliftUploadedFile } from '../../abstractions/UpliftUploadedFile.interface';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';
import { browserHistory } from '@/routing/browserHistory';
import AgGridLoadingOverlay
  from '@/components/common/TableGrid/CustomComponents/AgGridLoadingOverlay/AgGridLoadingOverlay';

const Verifying_Status = 'Verifying';
const REFRESH_GRID_INTERVAL = 10000;

export default function UploadedFilesTable() {
  const gridRef = useRef<AgGridReact>(null);
  const { getLinkWithQuery, getValue } = useAddQueryToUrl();
  const currentPage = parseInt(getValue('page') ?? '1', 10);
  const columns = useColumns();

  const updateDataIfCurrentPageIsIncorrect = (response: UpliftPaginatedList<UpliftUploadedFile>) => {
    if (response && response.totalPages < currentPage && response.totalPages >= 1) {
      const routePath = getLinkWithQuery({ page: response.totalPages.toString() });
      browserHistory.push(routePath);
    }
  };

  const onClickPage = (page: number) => {
    const routePath = getLinkWithQuery({ page: page.toString() });
    browserHistory.push(routePath);
  };

  const { getUploadedFiles, isLoading, totalPages, files } =
    useGetUploadedUpliftFiles(updateDataIfCurrentPageIsIncorrect);
  const verifyingStatus: boolean = useMemo(() => files.some(file => file.status === Verifying_Status), [files]);

  useEffect(() => {
    const setOverlay = () => {
      if (isLoading) {
        return gridRef.current?.api?.showLoadingOverlay();
      }

      if (!files?.length) {
        return gridRef.current?.api?.showNoRowsOverlay();
      }

      return gridRef.current?.api?.hideOverlay();
    };

    setOverlay();
  }, [isLoading, files]);

  useEffect(() => {
    getUploadedFiles(currentPage, UPLIFT_UPLOADED_FILES_PER_PAGE);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {

    const interval = setInterval(() => {

      if (verifyingStatus) {
        getUploadedFiles(currentPage, UPLIFT_UPLOADED_FILES_PER_PAGE);
      }
    }, REFRESH_GRID_INTERVAL);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyingStatus]);


  return (
    <>
      <TableGrid
        ref={gridRef}
        columnDefs={columns}
        rowData={files}
        suppressMovableColumns={true}
        loadingOverlayComponent={AgGridLoadingOverlay}
        defaultColDef={{ sortable: false, resizable: false }}
      />

      {totalPages > 1 && (
        <div className="d-flex justify-content-end pt-10 pb-10">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onClickPage={onClickPage}
          />
        </div>
      )}
    </>
  );
}
